import { useEffect, useState, useCallback } from 'react';

interface VersionInfo {
  currentVersion: string;
  latestVersion: string;
  hasUpdate: boolean;
}

export function useVersionCheck() {
  const [versionInfo, setVersionInfo] = useState<VersionInfo>({
    currentVersion: __APP_VERSION__,
    latestVersion: __APP_VERSION__,
    hasUpdate: false,
  });
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);

  // 检查版本更新
  const checkForUpdates = useCallback(async () => {
    // 在开发模式下跳过版本检查
    if (import.meta.env.DEV) {
      console.log('[VersionCheck] 开发模式下跳过版本检查');
      return;
    }

    try {
      // 添加时间戳防止缓存
      const timestamp = Date.now();
      const response = await fetch(`/version.json?t=${timestamp}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
        },
      });

      if (!response.ok) {
        console.log('[VersionCheck] 版本文件不存在，跳过检查');
        return;
      }

      const data = await response.json();
      const latestVersion = data.version;
      const currentVersion = __APP_VERSION__;

      console.log('[VersionCheck] 当前版本:', currentVersion, '最新版本:', latestVersion);

      if (latestVersion && latestVersion !== currentVersion) {
        console.log('[VersionCheck] 发现新版本:', latestVersion);
        setVersionInfo({
          currentVersion,
          latestVersion,
          hasUpdate: true,
        });
        setShowUpdatePrompt(true);
      }
    } catch (error) {
      console.log('[VersionCheck] 检查版本失败:', error);
    }
  }, []);

  // 执行更新 - 清除缓存并刷新页面
  const performUpdate = useCallback(async () => {
    console.log('[VersionCheck] 开始更新...');

    try {
      // 1. 注销 Service Worker
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map(async (registration) => {
            console.log('[VersionCheck] 注销 Service Worker:', registration.scope);
            await registration.unregister();
          })
        );
      }

      // 2. 清除 Cache Storage
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(async (cacheName) => {
            console.log('[VersionCheck] 删除缓存:', cacheName);
            await caches.delete(cacheName);
          })
        );
      }

      // 3. 清除 localStorage 中的版本相关数据（保留游戏存档）
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('version') || key.includes('cache'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));

      console.log('[VersionCheck] 缓存清理完成，准备刷新页面');

      // 4. 强制刷新页面（跳过缓存）
      window.location.reload();
    } catch (error) {
      console.error('[VersionCheck] 更新失败:', error);
      // 如果清理失败，直接强制刷新
      window.location.reload();
    }
  }, []);

  // 延迟检查更新（页面加载完成后 3 秒）
  useEffect(() => {
    // 页面可见性变化时检查更新
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdates();
      }
    };

    // 首次加载后延迟检查
    const timer = setTimeout(() => {
      checkForUpdates();
    }, 3000);

    // 页面重新可见时检查（比如用户切换回标签页）
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 定时检查（每 5 分钟）
    const interval = setInterval(() => {
      checkForUpdates();
    }, 5 * 60 * 1000);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkForUpdates]);

  return {
    versionInfo,
    showUpdatePrompt,
    setShowUpdatePrompt,
    performUpdate,
    checkForUpdates,
  };
}
